import { useState, useEffect } from 'react';
import { useTickets } from '@/hooks/useTickets';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge, PriorityBadge } from '@/components/shared/StatusBadge';
import { SlaTimer } from '@/components/shared/SlaTimer';
import { LoadingSpinner } from '@/components/shared/LoadingStates';
import { Link } from 'react-router-dom';
import { MapPin, User } from '@phosphor-icons/react';
import { formatRelativeTime } from '@/lib/utils';

export const FocDashboard = () => {
  const { data: tickets, isLoading } = useTickets({ status: 'approved' });
  const [activeTechCount, setActiveTechCount] = useState(0);

  useEffect(() => {
    const fetchTechs = async () => {
      try {
        const { usersApi } = await import('@/lib/api');
        const { data } = await usersApi.list({ role: 'technician' });
        setActiveTechCount((data || []).filter(t => t.is_active).length);
      } catch { /* ignore */ }
    };
    fetchTechs();
  }, []);

  const sortedTickets = tickets?.data
    ? [...tickets.data].sort((a, b) => {
        if (a.priority !== b.priority) {
          const priorityOrder = { urgent: 0, normal: 1, low: 2 };
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        }
        return new Date(a.sla_deadline) - new Date(b.sla_deadline);
      })
    : [];

  return (
    <DashboardLayout role="foc">
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-black tracking-tight mb-2">Assignment Queue</h1>
          <p className="text-muted-foreground">Assign technicians to approved service requests</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 border">
            <p className="label-micro mb-2">Pending Assignment</p>
            <p className="text-3xl font-black">{tickets?.data?.length || 0}</p>
          </Card>
          <Card className="p-4 border">
            <p className="label-micro mb-2">Urgent Priority</p>
            <p className="text-3xl font-black text-red-500">
              {tickets?.data?.filter(t => t.priority === 'urgent').length || 0}
            </p>
          </Card>
          <Card className="p-4 border">
            <p className="label-micro mb-2">SLA At Risk</p>
            <p className="text-3xl font-black text-yellow-600">
              {tickets?.data?.filter(t => {
                const deadline = new Date(t.sla_deadline);
                const now = new Date();
                const hoursLeft = (deadline - now) / (1000 * 60 * 60);
                return hoursLeft < 2 && hoursLeft > 0;
              }).length || 0}
            </p>
          </Card>
          <Card className="p-4 border">
            <p className="label-micro mb-2">Active Technicians</p>
            <p className="text-3xl font-black text-green-600">{activeTechCount}</p>
          </Card>
        </div>

        {/* Tickets List */}
        {isLoading ? (
          <LoadingSpinner />
        ) : sortedTickets?.length > 0 ? (
          <div className="space-y-3">
            {sortedTickets.map((ticket) => (
              <Card key={ticket.id} className="p-4" data-testid={`ticket-row-${ticket.ticket_number}`}>
                <div className="grid grid-cols-12 gap-4 items-center">
                  {/* Ticket Info */}
                  <div className="col-span-3">
                    <p className="font-bold mb-1">{ticket.ticket_number}</p>
                    <p className="text-sm text-muted-foreground">{ticket.customer_name}</p>
                  </div>

                  {/* Location */}
                  <div className="col-span-3">
                    <p className="text-sm flex items-start gap-1">
                      <MapPin size={16} className="mt-0.5 text-muted-foreground" />
                      <span className="line-clamp-2">{ticket.customer_address}</span>
                    </p>
                  </div>

                  {/* Priority */}
                  <div className="col-span-2">
                    <PriorityBadge priority={ticket.priority} />
                  </div>

                  {/* SLA Timer */}
                  <div className="col-span-2">
                    <SlaTimer deadline={ticket.sla_deadline} />
                  </div>

                  {/* Action */}
                  <div className="col-span-2 text-right">
                    <Link to={`/ops/foc/tickets/${ticket.id}`}>
                      <Button size="sm" data-testid={`assign-btn-${ticket.ticket_number}`}>
                        <User size={16} className="mr-2" />
                        Assign
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">No tickets waiting for assignment</p>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};